// Lint: imports reordered to satisfy import/order.
import React, { FC } from 'react';

import { RiskContextResponse } from '@ariksa/inventory-core/api';
import { Box, Stack } from '@chakra-ui/react';
import { join } from 'lodash';
import { IQueryState } from 'services/utils/QueryState';
import { VulnerabilityContainerInfo } from 'services/Vulnerability/types';

import {
  getIcon,
  renderRiskContext,
  renderTableHeaderWithLoader,
  renderTooltipFieldValue,
  StackedCell,
  StackedHeader,
  WithResourceIcon,
} from 'components/DataDisplay';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { TableColumnProps } from 'components/DataDisplay/NewTable/types';
import { IconTypes } from 'components/Icons';
import { Drawer } from 'components/Overlay';

interface Props {
  isOpen: boolean;
  onClose();
  row: any;
  containerRiskContext: IQueryState<Record<string, RiskContextResponse[]>>;
  ecrImageContainers: IQueryState<VulnerabilityContainerInfo[]>;
}

export const ECRImageContainersComponent: FC<Props> = props => {
  const {
    isOpen,
    onClose,
    row,
    containerRiskContext,
    ecrImageContainers,
  } = props;

  const columns: TableColumnProps[] = [
    {
      header: <Box pl={12}>Container</Box>,
      accessor: 'container_id',
      render: ({ value }) => (
        <WithResourceIcon resourceType="Container">{value}</WithResourceIcon>
      ),
    },
    {
      header: 'Account',
      accessor: 'account_id',
      align: 'left',
      render: ({ row, value }) => (
        <StackedCell
          upper={<Box boxSize={5}>{getIcon(row?.account_type)}</Box>}
          lower={value}
        />
      ),
    },
    {
      header: 'Deployed In',
      accessor: 'instance_id',
      align: 'left',
      render: ({ row, value }) => (
        <StackedCell upper={value} lower={row?.region} />
      ),
    },
    {
      header: 'Pod / Namespace',
      accessor: 'pod',
      align: 'left',
      render: ({ row }) => (
        <StackedCell
          upper={row?.pod}
          lower={row?.namespace}
          tooltip={
            <Stack>
              {renderTooltipFieldValue('Pod', row?.pod ?? '-')}
              {renderTooltipFieldValue('Namespace', row?.namespace ?? '-')}
            </Stack>
          }
        />
      ),
    },
    {
      header: 'IP Address',
      accessor: 'ip_address',
      align: 'left',
    },
    {
      header: 'OS',
      accessor: 'os',
      align: 'left',
      render: ({ row }) => (
        <StackedCell
          upper={row?.os_name}
          lower={row?.os_version}
          tooltip={
            <Stack>
              {renderTooltipFieldValue('Name', row?.os_name)}
              {renderTooltipFieldValue('Version', row?.os_version)}
            </Stack>
          }
        />
      ),
    },
    {
      header: renderTableHeaderWithLoader(
        'Risk Context',
        containerRiskContext.isLoading,
      ),
      render: ({ row }) =>
        renderRiskContext(containerRiskContext.data?.[row?.resource_uuid]),
      align: 'left',
    },
  ];

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      closeButton
      header={
        <Box>
          <StackedHeader
            upper={row?.name ?? join(row?.image_tags, ', ')}
            lower={row?.uri}
            type={IconTypes.EcrImage}
          />
        </Box>
      }
      body={
        <Box h={'full'}>
          <Table
            data={ecrImageContainers?.data ?? []}
            columns={columns}
            isLoading={ecrImageContainers?.isLoading}
          />
        </Box>
      }
      styles={{ content: { maxW: '1290px' } }}
    />
  );
};
